import { JotType, QuestionType, QuestionUsageType, WidgetState } from 'consts';
import React, { useEffect } from 'react';
import { findInArrayByKeyValue, isArray, isNullOrUndefOrEmpty, toObject } from 'utils';

import Caption from 'components/results/Caption';
import DecorativeImage from 'components/decorative-image/DecorativeImage';
import MatrixResult from 'components/results/MatrixResult';
import OptionResult from 'components/results/OptionResult';
import PollBy from 'components/poll-by/PollBy';
import PropTypes from 'prop-types';
import Recommend from 'components/recommend/Recommend';
import SpectrumResult from 'components/results/SpectrumResult';
import { actions } from 'actions';
import classNames from 'classnames';
import { connect } from 'unistore/preact';
import i18n from 'micro-i18n';

//FIXME: SOF-617 requires the following imports to be moved to components/widget-components.scss
// due to lazy loading of the widget inside an iframe. Keeping this here for potential fix down the road.
// import 'components/results/Result.scss';

/**
 *
 * @param {Object} [finishData]
 * @param {Object} [questionsData]
 * @param {Object} result
 * @param {{[isHidden]:boolean,[hasOnlyOne]:boolean,[hasNext]:boolean,[hasPrevious]:boolean,
 * [hasPreviousQuestion]:boolean,[hasFinish]:boolean,[hasAdFinale]:boolean,[isActive]:boolean}} resultState
 * @param {string} [portalUrl]
 * @param {string} [learnUrl]
 * @param {string} [partnerName]
 * @param {function} onRestart
 * @param {function} onNextResult
 * @param {function} onPreviousQuestionFromResult
 * @param {function} onPreviousResult
 * @param {function} onAfterShowingResults
 * @param {function} setWidgetState
 * @param {function} jot
 * @param {React.Ref} topFocusRef
 * @param {boolean} [pollByEnabled]
 * @param {boolean} [creEnabled]
 * @param {boolean} [testContentRecAtEndEnabled]
 * @param {boolean} [isCarousel]
 * @return {JSX.Element|null}
 * @constructor
 */
const Result = ({
  finishData,
  questionsData,
  result,
  resultState = {},
  portalUrl,
  learnUrl,
  partnerName,
  onRestart,
  onNextResult,
  onPreviousQuestionFromResult,
  onPreviousResult,
  onAfterShowingResults,
  setWidgetState,
  jot,
  topFocusRef,
  pollByEnabled = false,
  creEnabled = false,
  testContentRecAtEndEnabled = false,
  isCarousel = false
}) => {
  if (!result) {
    return null;
  }

  const { text, type, id: resultId, caption, total, img, imgAlt } = result;
  const { contentRec = [] } = toObject(finishData);
  const { questions = [] } = toObject(questionsData);
  const questionForResult = findInArrayByKeyValue(questions, 'id', resultId);
  const {
    isHidden, hasAdFinale, hasFinish, hasPrevious,
    hasPreviousQuestion, hasNext, hasOnlyOne, isActive = false
  } = resultState;
  const rLabelId = `csw-r-${resultId}`;
  const showResponseTotals = total > 50;
  const canJot = !isHidden && hasFinish && isActive;
  const isPinned = !isNullOrUndefOrEmpty(questionForResult)
    && questionForResult.usage === QuestionUsageType.Pinned;

  const hasRecommendations = isArray(contentRec) && contentRec.length > 0;
  const hasPortalUrl = !isNullOrUndefOrEmpty(portalUrl);
  const hasPortalFinalSlideConditions = hasFinish && !hasAdFinale;
  const hasPortal = hasPortalUrl && hasPortalFinalSlideConditions;
  const hasMore = !hasPortalUrl && hasPortalFinalSlideConditions;

  //FIXME: COL-60 Do we want to jot that WidgetState.ContentRecSlide was triggered?
  const onShowRecs = () => setWidgetState(WidgetState.ContentRecSlide);

  const onPortalClick = () => {
    // Track user clicking on the portal link
    jot({
      space: 'poll',
      type: JotType.EndUsersiteLink,
    });
  };

  useEffect(() => {
    canJot && jot({
      space: 'poll',
      type: JotType.LastResult,
      data: {
        more: hasMore,
        portal: hasPortal,
        aFin: hasAdFinale
      }
    });
  }, [canJot]);

  const ResultPortalButton = () => {
    return (
      <a className={`csw-btn csw-btn-primary csw-btn-next outline`}
         target="_blank"
         rel="noopener noreferrer"
         onClick={onPortalClick}
         href={portalUrl}>
        {i18n.t(hasFinish ? 'BUTTON_GO' : 'BUTTON_ANSWER_MORE_QUESTIONS')}
      </a>
    );
  };

  const ResultRestartButton = () => {
    const restartButtonExtraClassName = 'csw-btn-next';
    const restartButtonTextKey = hasOnlyOne ? 'BUTTON_ANSWER_MORE_QUESTIONS' : 'BUTTON_MORE';
    const hasContentRecSlide = testContentRecAtEndEnabled && hasRecommendations
      && restartButtonTextKey === 'BUTTON_MORE';
    return (
      <button type="button"
              className={`csw-btn csw-btn-primary csw-btn-restart ${restartButtonExtraClassName} outline`}
              onClick={hasContentRecSlide ? onShowRecs : onRestart}
              tabIndex="0">
        {i18n.t(restartButtonTextKey)}
      </button>
    );
  };

  const ResultBackButton = () => {
    return (
      <button type="button"
              className="csw-btn csw-btn-first csw-btn-prev outline"
              tabIndex="0"
              onClick={hasPreviousQuestion ? onPreviousQuestionFromResult
                : hasPrevious ? onPreviousResult : undefined}>{i18n.t('BUTTON_BACK')}
      </button>
    );
  };

  return (
    <div className="csw-result">
      <div className={classNames('csw-result-wrapper csw-scroll-section', {
        ' csw-result-hidden': isHidden,
      })}>

        <div
          className="csw-result-question-text csw-title-text outline"
          id={rLabelId}
          role="heading"
          aria-level="1"
          title={text}
          tabIndex="0"
          ref={isActive ? topFocusRef : null}
        >
          {img && (
            <DecorativeImage className="csw-result-question-text-image" src={img} alt={imgAlt || ''} />
          )}
          <span>{text}</span>
        </div>

        {(type === QuestionType.Radio || type === QuestionType.Checkbox) &&
          <OptionResult result={result} isActive={isActive} />}

        {type === QuestionType.Matrix &&
          <MatrixResult result={result} isActive={isActive} />}

        {type === QuestionType.Spectrum &&
          <SpectrumResult result={result} />}

        {showResponseTotals && <div className="csw-result-response-total">
          <span className="csw-result-response-total-text">
            {i18n.t('RESULT_RESPONSE_TOTAL', { total: Number(total).toLocaleString() })}
          </span>

          {!isNullOrUndefOrEmpty(learnUrl) &&
            <a className="csw-learn-more-link outline"
              target="_blank"
              rel="noopener noreferrer"
              href={learnUrl}>{i18n.t('LEARN_MORE')}</a>}
        </div>}

        {caption &&
          <Caption captionType={caption.type} captionBody={caption.body} translateBody={caption.translateBody} />}

      </div>

      <div className="csw-scroll-shadow" />

      {isActive &&
        <>
          <div className="csw-result-btns csw-btns csw-stretch">
            {hasNext &&
              <button type="button"
                className={`csw-btn csw-btn-primary csw-btn-next outline`}
                tabIndex="0"
                onClick={onNextResult}>{i18n.t('BUTTON_NEXT')}
              </button>}

            {hasAdFinale &&
              <button type="button"
                className="csw-btn csw-btn-primary csw-btn-next outline"
                tabIndex="0"
                onClick={onAfterShowingResults}>
                {i18n.t('BUTTON_MORE')}
              </button>}

            {(hasPrevious || hasPreviousQuestion) && <ResultBackButton />}

            {hasMore && <ResultRestartButton />}

            {hasPortal && <ResultPortalButton />}

          </div>

          {(pollByEnabled)
            && <PollBy id="result-poll-by"
                       partnerName={partnerName}
                       hasTooltip={isPinned} />}
        </>}

      <Recommend creEnabled={creEnabled}
                 testContentRecAtEndEnabled={testContentRecAtEndEnabled}
                 isCarousel={isCarousel} />

    </div>
  );
};

Result.propTypes /* remove-proptypes */ = {
  questionsData: PropTypes.object,
  finishData: PropTypes.object,
  result: PropTypes.object.isRequired,
  resultState: PropTypes.object.isRequired,
  portalUrl: PropTypes.string,
  learnUrl: PropTypes.string,
  focusable: PropTypes.bool,
  onRestart: PropTypes.func.isRequired,
  onNextResult: PropTypes.func.isRequired,
  onPreviousQuestionFromResult: PropTypes.func.isRequired,
  onPreviousResult: PropTypes.func.isRequired,
  onAfterShowingResults: PropTypes.func.isRequired,
  setWidgetState: PropTypes.func.isRequired,
  jot: PropTypes.func.isRequired,
  topFocusRef: PropTypes.object.isRequired,
  pollByEnabled: PropTypes.bool,
  partnerName: PropTypes.string,
  creEnabled: PropTypes.bool,
  testContentRecAtEndEnabled: PropTypes.bool,
  isCarousel: PropTypes.bool
};

export default connect('finishData,questionsData', actions)(Result);
